import * as React from 'react'
import { bind } from 'classnames/bind'
import { pickBy } from 'ramda'
import * as stylesBox from '@procore/core-css/src/layouts/_box.scss'
import * as stylesFlex from '@procore/core-css/src/layouts/_flex.scss'

import { SizeNS, Props, AccessibleProps, DivAttributes } from '../utils/types'

export namespace SideShorthandNS {
  export const b = 'b'
  export const l = 'l'
  export const r = 'r'
  export const t = 't'
}

export namespace BoxDisplayNS {
  export const block = 'block'
  export const inline = 'inline'
  export const inlineBlock = 'inline-block'
  export const flexColumn = 'flex-column'
  export const flexRow = 'flex-row'
  export const flexInlineColumn = 'flex-inline-column'
  export const flexInlineRow = 'flex-inline-row'
}

export namespace BoxSpaceAttributeNS {
  export const margin = 'margin'
  export const padding = 'padding'
}

export namespace InlineFlexStyleKeysNS {
  export const flex = 'flex'
  export const flexBasis = 'flexBasis'
  export const flexGrow = 'flexGrow'
  export const flexShrink = 'flexShrink'
  export const order = 'order'
}

export namespace FlexAlignNS {
  export const none = ''
  export const baseline = 'baseline'
  export const center = 'center'
  export const end = 'flex-end'
  export const normal = 'normal'
  export const start = 'flex-start'
  export const stretch = 'stretch'
}

export namespace FlexDirectionNS {
  export const column = 'column'
  export const columnReverse = 'column-reverse'
  export const row = 'row'
  export const rowReverse = 'row-reverse'
}

export namespace FlexJustifyNS {
  export const none = ''
  export const center = 'center'
  export const end = 'flex-end'
  export const spaceAround = 'space-around'
  export const spaceBetween = 'space-between'
  export const spaceEvenly = 'space-evenly'
  export const start = 'flex-start'
  export const stretch = 'stretch'
}

export namespace FlexWrapNS {
  export const nowrap = 'nowrap'
  export const wrapReverse = 'wrap-reverse'
  export const wrap = 'wrap'
}

export namespace TextAlignNS {
  export const center = 'center'
  export const left = 'left'
  export const right = 'right'
}

type SideShorthand =
  | typeof SideShorthandNS.b
  | typeof SideShorthandNS.l
  | typeof SideShorthandNS.r
  | typeof SideShorthandNS.t

type BoxDisplay =
  | typeof BoxDisplayNS.block
  | typeof BoxDisplayNS.inline
  | typeof BoxDisplayNS.inlineBlock
  | typeof BoxDisplayNS.flexColumn
  | typeof BoxDisplayNS.flexRow
  | typeof BoxDisplayNS.flexInlineColumn
  | typeof BoxDisplayNS.flexInlineRow

type BoxSpaceAttribute =
  | typeof BoxSpaceAttributeNS.margin
  | typeof BoxSpaceAttributeNS.padding

type InlineFlexStyleKeys =
  | typeof InlineFlexStyleKeysNS.flex
  | typeof InlineFlexStyleKeysNS.flexBasis
  | typeof InlineFlexStyleKeysNS.flexGrow
  | typeof InlineFlexStyleKeysNS.flexShrink
  | typeof InlineFlexStyleKeysNS.order

type FlexAlign =
  | typeof FlexAlignNS.none
  | typeof FlexAlignNS.baseline
  | typeof FlexAlignNS.center
  | typeof FlexAlignNS.end
  | typeof FlexAlignNS.normal
  | typeof FlexAlignNS.start
  | typeof FlexAlignNS.stretch

export type FlexDirection =
  | typeof FlexDirectionNS.column
  | typeof FlexDirectionNS.columnReverse
  | typeof FlexDirectionNS.row
  | typeof FlexDirectionNS.rowReverse

type FlexJustify =
  | typeof FlexJustifyNS.none
  | typeof FlexJustifyNS.center
  | typeof FlexJustifyNS.end
  | typeof FlexJustifyNS.spaceAround
  | typeof FlexJustifyNS.spaceBetween
  | typeof FlexJustifyNS.spaceEvenly
  | typeof FlexJustifyNS.start
  | typeof FlexJustifyNS.stretch

export type FlexWrap =
  | typeof FlexWrapNS.nowrap
  | typeof FlexWrapNS.wrapReverse
  | typeof FlexWrapNS.wrap

type TextAlign =
  | typeof TextAlignNS.center
  | typeof TextAlignNS.left
  | typeof TextAlignNS.right

export type MarginSize =
  | typeof SizeNS.auto
  | typeof SizeNS.none
  | typeof SizeNS.xs
  | typeof SizeNS.sm
  | typeof SizeNS.md
  | typeof SizeNS.lg
  | typeof SizeNS.xl
  | typeof SizeNS.xxl

type PaddingSize =
  | typeof SizeNS.none
  | typeof SizeNS.xs
  | typeof SizeNS.sm
  | typeof SizeNS.md
  | typeof SizeNS.lg
  | typeof SizeNS.xl
  | typeof SizeNS.xxl

export interface MarginOptions {
  margin?: MarginSize | string
  marginBottom?: MarginSize
  marginLeft?: MarginSize
  marginRight?: MarginSize
  marginTop?: MarginSize
}

export interface PaddingOptions {
  padding?: PaddingSize | string
  paddingBottom?: PaddingSize
  paddingLeft?: PaddingSize
  paddingRight?: PaddingSize
  paddingTop?: PaddingSize
}

export interface FlexOptions {
  alignContent?: FlexAlign
  alignItems?: FlexAlign
  alignSelf?: FlexAlign
  justifyContent?: FlexJustify
}

export interface BoxProps
  extends Props,
    AccessibleProps,
    FlexOptions,
    MarginOptions,
    PaddingOptions {
  display?: BoxDisplay
  flex?: string
  flexBasis?: string
  flexGrow?: string
  flexShrink?: string
  flexWrap?: FlexWrap
  order?: number
  textAlign?: TextAlign
}

type Size = MarginSize | PaddingSize | undefined

const cx = bind({ ...stylesBox, ...stylesFlex })

const pickDefined = pickBy(v => v !== undefined)

const getMeasurementClass = (
  attribute: BoxSpaceAttribute,
  side: SideShorthand,
  value: Size
) =>
  value && value !== SizeNS.none ? `core-${attribute}-${side}-${value}` : ''

const mapToClasses = (attribute: BoxSpaceAttribute) => (
  measurements: Size[]
) => [
  getMeasurementClass(attribute, SideShorthandNS.t, measurements[0]),
  getMeasurementClass(attribute, SideShorthandNS.r, measurements[1]),
  getMeasurementClass(attribute, SideShorthandNS.b, measurements[2]),
  getMeasurementClass(attribute, SideShorthandNS.l, measurements[3]),
]

const mapOverrides = (top: Size, right: Size, bottom: Size, left: Size) => (
  measurements: Size[]
) => {
  if (top) {
    measurements[0] = top
  }
  if (right) {
    measurements[1] = right
  }
  if (bottom) {
    measurements[2] = bottom
  }
  if (left) {
    measurements[3] = left
  }

  return measurements
}

const measurementStringToArray = (measurementString: string = '') => {
  const tokenized: Size[] = measurementString.split(' ') as Size[]

  let measurements: Size[] = []

  if (tokenized.length === 1) {
    measurements = [tokenized[0], tokenized[0], tokenized[0], tokenized[0]]
  } else if (tokenized.length === 2) {
    measurements = [tokenized[0], tokenized[1], tokenized[0], tokenized[1]]
  } else if (tokenized.length === 3) {
    measurements = [tokenized[0], tokenized[1], tokenized[2], tokenized[1]]
  } else if (tokenized.length === 4) {
    measurements = tokenized
  }

  return measurements
}

const Box = React.forwardRef<HTMLDivElement, DivAttributes & BoxProps>(
  (
    {
      alignContent,
      alignItems,
      alignSelf,
      className,
      display,
      flex,
      flexBasis,
      flexGrow,
      flexShrink,
      flexWrap,
      justifyContent,
      style,
      order,
      margin = 'none',
      marginTop,
      marginRight,
      marginBottom,
      marginLeft,
      padding = 'none',
      paddingTop,
      paddingRight,
      paddingBottom,
      paddingLeft,
      textAlign,
      children,
      ...props
    },
    ref
  ) => {
    const marginClasses =
      margin || marginRight || marginLeft || marginTop || marginBottom
        ? mapToClasses(BoxSpaceAttributeNS.margin)(
            mapOverrides(marginTop, marginRight, marginBottom, marginLeft)(
              measurementStringToArray(margin)
            )
          )
        : []

    const paddingClasses =
      padding || paddingRight || paddingLeft || paddingTop || paddingBottom
        ? mapToClasses(BoxSpaceAttributeNS.padding)(
            mapOverrides(paddingTop, paddingRight, paddingBottom, paddingLeft)(
              measurementStringToArray(padding)
            )
          )
        : []

    return (
      <div
        ref={ref}
        className={cx(
          ...marginClasses,
          ...paddingClasses,
          alignContent && `core-flex-align-content-${alignContent}`,
          alignItems && `core-flex-align-items-${alignItems}`,
          alignSelf && `core-flex-align-self-${alignSelf}`,
          display && `core-${display}`,
          flexWrap && `core-flex-wrap-${flexWrap}`,
          justifyContent && `core-flex-justify-content-${justifyContent}`,
          textAlign && `core-text-${textAlign}`,
          className
        )}
        style={{
          ...style,
          ...pickDefined({ flex, flexBasis, flexGrow, flexShrink, order }),
        }}
        {...props}
      >
        {children}
      </div>
    )
  }
)

Box.displayName = 'Box'

export default Box
